import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import CommunicationsService, {
  CommunicationsStats,
  Message,
  MessageFilters,
} from "@/app/_services/CommunicationsService";
import { toast } from "@/app/_utils/toast";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export function useCommunicationsModel() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [stats, setStats] = useState<CommunicationsStats | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const [selected, setSelected] = useState<Message | null>(null);

  // Set by the "View all" link on a guest's detail page. Read from the URL rather than
  // held in state so the filtered view is linkable and survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const guestId = searchParams.get("guest_id") ?? "";

  // Filters change faster than requests return. Without this, a slow first page can
  // land after a later one and repopulate the table with stale rows.
  const requestId = useRef(0);

  const loadFirstPage = useCallback(
    async (filters: MessageFilters) => {
      const id = ++requestId.current;
      setIsLoading(true);
      try {
        const page = await CommunicationsService.getMessagesAsync({
          ...filters,
          limit: PAGE_SIZE,
        });
        if (id !== requestId.current) return;
        setMessages(page.data);
        setCursor(page.next_cursor);
      } catch (error) {
        console.error(error);
        if (id === requestId.current) toast.error("Could not load communications");
      } finally {
        if (id === requestId.current) setIsLoading(false);
      }
    },
    [],
  );

  // Debounced so typing a recipient address doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      loadFirstPage({
        search,
        channel: channelFilter,
        status: statusFilter,
        source: sourceFilter,
        guest_id: guestId,
      });
    }, search ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(handle);
  }, [search, channelFilter, statusFilter, sourceFilter, guestId, loadFirstPage]);

  useEffect(() => {
    CommunicationsService.getStatsAsync()
      .then(setStats)
      .catch((error) => console.error(error));
  }, []);

  async function loadMore() {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const page = await CommunicationsService.getMessagesAsync({
        search,
        channel: channelFilter,
        status: statusFilter,
        source: sourceFilter,
        guest_id: guestId,
        cursor,
        limit: PAGE_SIZE,
      });
      // Keyset paging means the server cannot hand back a row we already have, but
      // guard anyway — a stale cursor from a rapid filter change could.
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return prev.concat(page.data.filter((m) => !seen.has(m.id)));
      });
      setCursor(page.next_cursor);
    } catch (error) {
      console.error(error);
      toast.error("Could not load more messages");
    } finally {
      setIsLoadingMore(false);
    }
  }

  function clearFilters() {
    setSearch("");
    setChannelFilter("");
    setStatusFilter("");
    setSourceFilter("");
    if (guestId) {
      searchParams.delete("guest_id");
      setSearchParams(searchParams, { replace: true });
    }
  }

  const hasFilters = Boolean(
    search || channelFilter || statusFilter || sourceFilter || guestId,
  );

  return {
    messages,
    stats,
    isLoading,
    isLoadingMore,
    hasMore: cursor !== null,
    loadMore,
    search,
    setSearch,
    channelFilter,
    setChannelFilter,
    statusFilter,
    setStatusFilter,
    sourceFilter,
    setSourceFilter,
    hasFilters,
    clearFilters,
    guestId,
    selected,
    setSelected,
  };
}
